import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTypedSelector } from '../../../Hooks/useTypedSelector';
import { translate } from '../../../Services/translation';
import Input from '../../Inputs/Input';
import { get, post } from '../../../Services/http';
import { debounce } from 'lodash-es';
import ZhipsterDropdown from './ZhipsterDropdown';
import {
  setShippingConfirmed,
  setCustomerDetailsConfirmed,
} from '../../../Actions/Checkout.action';
import { useDispatch } from 'react-redux';
import LoadingSpinner from '../../LoadingSpinner';
import ZhipsterDropOptionRadio from './Zhipster.DropOptionRadio';

interface ZhipsterProps {
  onShippingOptionChanged: (
    dropOptionId: string | undefined,
    dropPointId: string | undefined,
    hasDropPoints: boolean
  ) => void;
  deliveryMethodsComplete?: boolean;
  onLoadingChanged: (isLoading: boolean) => void;
}

export interface ZhipsterDropOption {
  formattedPrice: string;
  formattedCampaignPrice?: string;
  name: string;
  description?: string;
  dropOptionId: string;
  dropPoints?: DropPoint[];
  forwarderId: string;
}

export interface DropPoint {
  DropPointID: string;
  Name: string;
  DistanceInKm: number;
  Address: ZhipsterAddress;
}

interface ZhipsterAddress {
  Name: string;
  Street1: string;
  Street2?: string;
  City: string;
  ZipCode: string;
  CountryCode: string;
  StateCode?: string;
}

const Zhipster = ({
  onShippingOptionChanged,
  deliveryMethodsComplete = false,
  onLoadingChanged,
}: ZhipsterProps) => {
  const dispatch = useDispatch();
  const { payload } = useTypedSelector((state) => state.checkout);
  const { selectedDeliveryMethod, customerDetails, alternativeAddress } =
    payload;

  const [abortController, setAbortController] =
    useState<AbortController>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedOptions, setHasFetchedOptions] = useState(false);
  const [selectedDropOptionId, setSelectedDropOptionId] = useState<
    string | undefined
  >(payload.zhipsterData?.selectedDropOptionId);
  const [dropOptions, setDropOptions] = useState<ZhipsterDropOption[]>([]);
  const [zipCode, setZipCode] = useState<string | undefined>(
    alternativeAddress?.zipCode ?? customerDetails?.zipCode
  );
  const [error, setError] = useState<string | undefined>();
  const [selectedDropPointId, setSelectedDropPointId] = useState<
    string | undefined
  >(payload.zhipsterData?.selectedDropPointId);

  const isZhipster = useMemo(
    () => selectedDeliveryMethod?.id == 'Zhipster:CheckoutApiV2',
    [selectedDeliveryMethod]
  );
  const selectedDropOption = useMemo(
    () =>
      dropOptions.find((opt) => opt.dropOptionId == selectedDropOptionId),
    [dropOptions, selectedDropOptionId]
  );
  const selectedDropPoint = useMemo(
    () =>
      selectedDropOption?.dropPoints?.find(
        (opt) => opt.DropPointID == selectedDropPointId
      ),
    [selectedDropOption, selectedDropPointId]
  );
  const dropPoints = useMemo(
    () =>
      selectedDropOption?.dropPoints?.map((drop) => ({
        value: drop.DropPointID,
        name: drop.Name,
        street: drop.Address.Street1,
        zipCode: drop.Address.ZipCode,
        city: drop.Address.City,
      })),
    [selectedDropOption]
  );

  useEffect(() => {
    if (dropOptions.length == 0) {
      if (alternativeAddress?.zipCode ?? customerDetails?.zipCode) {
        setZipCode(
          alternativeAddress?.zipCode ?? customerDetails?.zipCode
        );
      }
    }
  }, [alternativeAddress?.zipCode, customerDetails?.zipCode]);

  // Auto-select first drop option
  useEffect(() => {
    if (!selectedDropOptionId) {
      setSelectedDropOptionId(
        dropOptions ? dropOptions[0]?.dropOptionId : undefined
      );
    }
  }, [dropOptions]);

  // Auto-select first drop point
  useEffect(() => {
    if (!selectedDropPointId) {
      setSelectedDropPointId(
        dropPoints ? dropPoints[0]?.value : undefined
      );
    }
  }, [dropPoints]);

  const handleRequestError = async (err) => {
    setHasFetchedOptions(false);
    if (err.response) {
      const data = await err.response.json();
      setError(data.error ?? translate('zhipster.error.general'));
    } else {
      setError(translate('zhipster.error.no-response'));
    }
  };

  const fetchShippingOptions = () => {
    setIsLoading(true);
    get(`/api/zhipster?zipCode=${zipCode}`, abortController)
      .then((res) => res.json())
      .then((data: ZhipsterDropOption[]) => setDropOptions(data))
      .then(() => setHasFetchedOptions(true))
      .then(() => setError(undefined))
      .catch(handleRequestError)
      .finally(() => setIsLoading(false));
  };

  const submitSelectedDropOption = useCallback(
    debounce(() => {
      setIsLoading(true);
      post(
        '/api/zhipster',
        {
          selectedDropOptionId,
          selectedDropPointId,
          zipCode,
        },
        abortController
      )
        .then(() =>
          onShippingOptionChanged(
            selectedDropOptionId,
            selectedDropPointId,
            !!dropPoints && dropPoints.length > 0
          )
        )
        .then(() => setError(undefined))
        .catch(handleRequestError)
        .finally(() => setIsLoading(false));
    }, 300),
    [
      selectedDropOptionId,
      selectedDropPointId,
      onShippingOptionChanged,
      dropPoints,
      abortController,
    ]
  );

  useEffect(() => {
    if (!!selectedDropOptionId && isZhipster) {
      if (
        !dropPoints ||
        (dropPoints.length > 0 && !!selectedDropPointId)
      ) {
        submitSelectedDropOption();
      }
    }
  }, [selectedDropOptionId, selectedDropPointId]);

  useEffect(() => {
    if (!isZhipster) {
      abortController?.abort();
      setAbortController(new AbortController());
    }

    if (isZhipster && zipCode) {
      fetchShippingOptions();
    }
  }, [selectedDeliveryMethod]);

  useEffect(() => {
    if (!isZhipster) {
      setSelectedDropOptionId(undefined);
      setSelectedDropPointId(undefined);
      setDropOptions([]);
    }
  }, [isZhipster]);

  useEffect(() => {
    onLoadingChanged && onLoadingChanged(isLoading);
  }, [isLoading, onLoadingChanged]);

  return !deliveryMethodsComplete ? (
    <>
      {selectedDeliveryMethod?.id == 'Zhipster:CheckoutApiV2' ? (
        <div>
          <ZhipsterForm
            zipCode={zipCode}
            onZipCodeChange={(zipCode) => setZipCode(zipCode)}
            onSubmit={fetchShippingOptions}
            onReset={() => {
              setSelectedDropOptionId(undefined);
              setSelectedDropPointId(undefined);
              setZipCode(undefined);
              setDropOptions([]);
              dispatch(setCustomerDetailsConfirmed(false));
            }}
            isLoading={isLoading}
            showEditButton={hasFetchedOptions}
          />
          {error && <p className="mt-1 text-sm text-error-500">{error}</p>}
          {dropOptions.length > 0 && (
            <>
              <div className="grid grid-cols-1">
                {dropOptions.map((opt) => (
                  <ZhipsterDropOptionRadio
                    {...opt}
                    checked={opt.dropOptionId === selectedDropOptionId}
                    onChange={(option) => {
                      setSelectedDropOptionId(option.dropOptionId);
                      if (
                        !option.dropPoints ||
                        option.dropPoints.length == 0
                      ) {
                        setSelectedDropPointId(undefined);
                      } else {
                        setSelectedDropPointId(
                          option.dropPoints[0]?.DropPointID
                        );
                      }
                    }}
                    key={opt.dropOptionId + 'zhipster-drop-option'}
                  />
                ))}
              </div>
              {dropPoints && (
                <ZhipsterDropdown
                  size="large"
                  className="mt-2"
                  selectClassName="!border-gray-200"
                  placeholder={translate(
                    'zhipster.drop-point.placeholder'
                  )}
                  options={dropPoints}
                  selectedValue={selectedDropPointId}
                  onChange={({ value }) => setSelectedDropPointId(value)}
                />
              )}
            </>
          )}
        </div>
      ) : null}
    </>
  ) : selectedDropOption && isZhipster ? (
    <div
      className="cursor-pointer text-md font-light text-gray-500"
      onClick={() => dispatch(setShippingConfirmed(false))}
    >
      <div className="mb-2">
        {selectedDropOption.name}{' '}
        {selectedDropPoint && <>({selectedDropOption.description})</>}
      </div>
      {selectedDropPoint && (
        <div>
          {selectedDropPoint?.Name} ({selectedDropPoint?.Address.Street1},{' '}
          {selectedDropPoint?.Address.ZipCode}{' '}
          {selectedDropPoint?.Address.City})
        </div>
      )}
      {!selectedDropPoint && <div>{selectedDropOption.description}</div>}
    </div>
  ) : null;
};

interface ZhipsterFormProps {
  zipCode?: string;
  onZipCodeChange: (zipCode: string) => void;
  onSubmit: () => void;
  onReset: () => void;
  isLoading: boolean;
  showEditButton: boolean;
}

const ZhipsterForm = ({
  zipCode,
  onZipCodeChange,
  onSubmit,
  onReset,
  isLoading,
  showEditButton,
}: ZhipsterFormProps) => {
  const canSubmit = useMemo(
    () => !isLoading && !!zipCode,
    [zipCode, isLoading]
  );

  return (
    <div className="grow">
      <div className="mb-4 text-md">{translate('zhipster.help-text')}</div>
      <div className="mb-4 grid grid-cols-4 gap-4">
        <Input
          label={translate('checkout.customerinfo.zipcode')}
          autoComplete="postal-code"
          value={zipCode ?? ''}
          showAnimatedLabel={true}
          inputSize="xlarge"
          onKeyDown={(e) => e.key == 'Enter' && canSubmit && onSubmit()}
          onChange={(e) => onZipCodeChange(e.target.value)}
          wrapperClassName="col-span-3"
          disabled={isLoading || showEditButton}
        />
        <button
          className="btn btn--black btn--xlarge btn--fullwidth col-span-1"
          onClick={showEditButton ? onReset : onSubmit}
          disabled={!canSubmit}
        >
          {!isLoading && showEditButton && translate('general.edit')}
          {!isLoading &&
            !showEditButton &&
            translate('zhipster.get-options')}
          {isLoading && <LoadingSpinner />}
        </button>
      </div>
    </div>
  );
};

export default Zhipster;
